import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Producer } from "@/lib/producerTypes";
import { ProducerHeader } from "@/components/producer/ProducerHeader";
import { EventsSection } from "@/components/producer/EventsSection";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ProducerPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [producer, setProducer] = useState<Producer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    const fetchProducer = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching producer with slug:', slug);

        // First get the producer bio
        const { data: bioData, error: bioError } = await supabase
          .from('producers_bio')
          .select('*, title')
          .eq('slug', slug)
          .single();

        console.log('Bio fetch result:', { bioData, bioError });

        if (bioError) {
          console.error('Bio fetch error:', bioError);
          throw bioError;
        }

        // Then get the producer company name (optional)
        const { data: producerData } = await supabase
          .from('producers')
          .select('company_name')
          .eq('id', bioData.producer_id)
          .maybeSingle();

        console.log('Producer data fetch result:', { producerData });

        // Transform database data to match our interface
        const producer: Producer = {
          id: bioData.id,
          slug: bioData.slug,
          name: producerData?.company_name || bioData.slug.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' '),
          title: bioData.title,
          avatarUrl: bioData.avatar_url,
          websiteUrl: bioData.website_url,
          socials: bioData.socials as { tiktok?: string; facebook?: string; instagram?: string; x?: string; },
          bio: bioData.bio,
          followerCount: bioData.follower_count
        };

        console.log('Final producer object:', producer);
        setProducer(producer);
      } catch (err: any) {
        console.error('Producer fetch error:', err);
        setError(err.message || "Producer not found");
      } finally {
        setLoading(false);
      }
    };

    fetchProducer();
  }, [slug]);

  const handleFollowerCountUpdate = (newCount: number) => {
    if (producer) {
      setProducer(prev => prev ? { ...prev, followerCount: newCount } : null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <Header />
        <div className="flex-grow">
          <div className="container mx-auto px-4 py-8">
            <div className="space-y-8">
              <Skeleton className="h-64 w-full rounded-3xl bg-white/5" />
              <div className="space-y-4">
                <Skeleton className="h-12 w-64 mx-auto bg-white/5" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-80 rounded-lg bg-white/5" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="container mx-auto px-4">
            <Alert className="max-w-md mx-auto bg-red-500/10 border-red-500/20">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-300">
                {error}
              </AlertDescription>
            </Alert>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!producer) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="container mx-auto px-4">
            <Alert className="max-w-md mx-auto bg-yellow-500/10 border-yellow-500/20">
              <Sparkles className="h-4 w-4 text-yellow-400" />
              <AlertDescription className="text-yellow-300">
                Producer not found
              </AlertDescription>
            </Alert>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <SEO 
        title={`${producer.name} - Events & Bio`}
        description={producer.bio ? producer.bio.slice(0, 150) : `Discover events by ${producer.name} on Slay Events`}
        ogImage={producer.avatarUrl}
      />
      
      <Header />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-4 py-8 space-y-12">
          <ProducerHeader producer={producer} onFollowerCountUpdate={handleFollowerCountUpdate} />
          <EventsSection producerSlug={producer.slug} />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProducerPage;